import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Trash2, Plus, X, Flag, ChevronLeft, ChevronRight, ChevronDown, Eye, AlertTriangle, FileText, Camera, Pencil } from 'lucide-react';
import { toast } from 'react-hot-toast';
import DetailsSidebar from '../../../components/DetailsSidebar';
import EmergencyInfoModal from './EmergencyInfoModal';
import CareNeedsTab from './CareNeedsTab'; 
import RiskAssessmentTab from './RiskAssessmentTab';
import BodyMapTab from './BodyMapTab';
import AccidentIncidentTab from './AccidentIncidentTab';
import CareNotesTab from './CareNotesTab';
import MedicationTab from './MedicationTab';
import NutrientsTab from './NutrientsTab';
import ContinenceTab from './ContinenceTab';
import VitalsTab from './VitalsTab';
import BehaviourTab from './BehaviourTab';
import RemindersCard from '../RemindersCard';
import Modal from '../../../components/Modal';
import residencesService from '../../../services/carehome_manager/residencesService';
import Loader from '../../../components/Loader';
import { TabSkeleton } from '../../../skeletons';
import { flagTypeStyle } from '../../../utils/flagTypes';
import { TAB_BY_SLUG } from '../../../utils/reminderLinks';
import { toDocumentList, documentLabel } from '../../../utils/residenceDocuments';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { Select, DocumentViewer } from '../../../components';
import AddressAutocomplete from '../../../components/AddressAutocomplete';

// Address inputs whose edit should invalidate a previously resolved map pin.
const ADDRESS_FIELDS = [
    'contact_address_line_1', 'contact_address_line_2',
    'contact_city', 'contact_county_state', 'contact_postcode', 'contact_country',
];

// Origin for uploaded files (strip trailing /api from the axios base URL).
const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
// First letter of the name for the avatar fallback.
const initialOf = (name) => {
    const c = (name || '').trim().charAt(0);
    return /[a-zA-Z]/.test(c) ? c.toUpperCase() : 'U';
};
// Client avatar: the uploaded profile pic if present, otherwise the name initial.
const ClientAvatar = ({ url, name, className = '', textSize = 'text-2xl' }) => (
    url ? (
        <img
            src={url.startsWith('http') ? url : `${API_ORIGIN}${url}`}
            alt={name}
            className={`${className} rounded-lg object-cover border border-gray-200 dark:border-gray-700`}
        />
    ) : (
        <div className={`${className} rounded-lg bg-gray-100 dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 flex items-center justify-center ${textSize} font-bold text-gray-500 dark:text-gray-400`}>
            {initialOf(name)}
        </div>
    )
);

// The eight documents captured at client creation + friendly labels.
const DOCUMENT_FIELDS = [
  { key: 'photo_id_url', label: 'Photo ID' },
  { key: 'proof_of_address_url', label: 'Proof of address' },
  { key: 'nhs_exemption_certificate_url', label: 'NHS exemption certificate' },
  { key: 'additional_id_document_url', label: 'Additional ID document' },
  { key: 'signed_admission_contract_url', label: 'Signed admission contract' },
  { key: 'pre_admission_assessment_url', label: 'Pre-admission assessment' },
  { key: 'dnar_form_url', label: 'DNAR form' },
  { key: 'poa_certificate_url', label: 'POA certificate' },
];

/**
 * Client documents list.
 *
 * A document type can hold several files. One file opens straight away; two or
 * more collapse into an accordion row that expands to show each file, so the
 * list stays one row per document type instead of repeating the same label.
 * Files open in the in-page DocumentViewer (via `onOpen`), never a new tab.
 */
const ClientDocuments = ({ docs = {}, onOpen }) => {
    const open = (path, title) => onOpen({ url: `${API_ORIGIN}${path}`, name: documentLabel(path), title });
    const [expanded, setExpanded] = useState({});

    // The additional slot holds any number of named documents, so it expands
    // into one row per document under the manager's own name for each. Listing
    // them under a single label hid everything after the first.
    const additionalRows = (Array.isArray(docs.additional_documents) ? docs.additional_documents : [])
        .map((row, index) => ({
            key: `additional_${index}`,
            label: row.name?.trim() || 'Additional document',
            files: toDocumentList(row.files),
        }))
        .filter((row) => row.files.length > 0);

    const present = DOCUMENT_FIELDS
        .filter((d) => d.key !== 'additional_id_document_url')
        .map((d) => ({ ...d, files: toDocumentList(docs[d.key]) }))
        .filter((d) => d.files.length > 0)
        .concat(additionalRows);

    if (present.length === 0) {
        return <p className="text-[13px] text-gray-500 dark:text-gray-400 py-6 text-center">No documents were uploaded for this client.</p>;
    }

    const rowClass = 'flex items-center justify-between gap-3 w-full border border-[#E5EAF3] dark:border-gray-700 rounded-lg px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#31373f] transition';
    const labelClass = 'flex items-center gap-2 text-[13px] font-medium text-gray-800 dark:text-gray-200 min-w-0';

    return (
        <div className="space-y-2">
            {present.map((d) => {
                if (d.files.length === 1) {
                    return (
                        <button key={d.key} type="button" onClick={() => open(d.files[0], d.label)} className={rowClass}>
                            <span className={labelClass}>
                                <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                                <span className="truncate">{d.label}</span>
                            </span>
                            <Eye className="w-4 h-4 text-gray-400 shrink-0" />
                        </button>
                    );
                }

                const isOpen = !!expanded[d.key];
                return (
                    <div key={d.key}>
                        <button
                            type="button"
                            onClick={() => setExpanded((prev) => ({ ...prev, [d.key]: !prev[d.key] }))}
                            aria-expanded={isOpen}
                            className={`${rowClass} ${isOpen ? 'rounded-b-none border-b-0' : ''}`}
                        >
                            <span className={labelClass}>
                                <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                                <span className="truncate">{d.label}</span>
                                <span className="shrink-0 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/15 text-[#2563EB] dark:text-blue-400 tabular-nums">
                                    {d.files.length} files
                                </span>
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isOpen && (
                            <div className="border border-t-0 border-[#E5EAF3] dark:border-gray-700 rounded-b-lg divide-y divide-[#F1F5F9] dark:divide-gray-700 overflow-hidden">
                                {d.files.map((path, i) => (
                                    <button
                                        key={`${d.key}-${i}`}
                                        type="button"
                                        onClick={() => open(path, `${d.label} · ${i + 1} of ${d.files.length}`)}
                                        className="flex items-center justify-between gap-3 w-full text-left pl-8 pr-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                                    >
                                        <span className="flex items-center gap-2 min-w-0">
                                            <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500 tabular-nums shrink-0">{i + 1}.</span>
                                            {/* The stored filename is the only per-file
                                                detail we have, so show it here. */}
                                            <span className="text-[12.5px] text-gray-700 dark:text-gray-300 truncate" title={documentLabel(path)}>
                                                {documentLabel(path)}
                                            </span>
                                        </span>
                                        <Eye className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {docs.document_notes && (
                <div className="mt-3 pt-3 border-t border-[#F1F5F9] dark:border-gray-700">
                    <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Notes</p>
                    <p className="text-[13px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{docs.document_notes}</p>
                </div>
            )}
        </div>
    );
};

// Reusable Field Component
const DetailField = ({ label, value }) => (
    <div className="flex flex-col">
        <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mb-1">{label}</span>
        <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200">{value || '-'}</span>
    </div>
);

// Editable Field Component
const EditableField = ({ label, value, fieldName, type = "text", isDateField = false, isEditMode, editFormData, handleEditFormChange }) => {
    if (!isEditMode) {
        return <DetailField label={label} value={value} />;
    }

    // For date inputs, use the raw value (YYYY-MM-DD format), not formatted
    const inputValue = isDateField && editFormData[fieldName] 
        ? editFormData[fieldName].split('T')[0] 
        : (editFormData[fieldName] || '');

    return (
        <div className="flex flex-col">
            <label className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mb-1.5">{label}</label>
            <input
                type={type}
                name={fieldName}
                value={inputValue}
                onChange={handleEditFormChange}
                className="px-3 py-2 bg-white dark:bg-[#3a4048] border border-gray-300 dark:border-gray-600 rounded-lg text-[13px] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                placeholder={label}
            />
        </div>
    );
};

// Card wrapper for a labelled section in the read-only view modal
const ViewSection = ({ title, children, cols = 'grid-cols-2 md:grid-cols-3' }) => (
    <div className="mb-6">
        <h3 className="text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-3">{title}</h3>
        <div className="bg-white dark:bg-[#31373f] border border-[#E5EAF3] dark:border-gray-700 rounded-lg p-5">
            <div className={`grid ${cols} gap-y-5 gap-x-4`}>
                {children}
            </div>
        </div>
    </div>
);

// Read-only "View full details" modal — renders every section of the resident
// record returned by getResidenceById (the same data the edit form uses).
const ResidentDetailsViewModal = ({ data, flags = [], formatDate, onClose, onEdit }) => {
    const { user } = useAuth();
    const p = data?.personalDetails || {};
    const c = data?.contactDetails || {};
    const m = data?.medicalDetails || {};
    const f = data?.financeDetails || {};
    const a = data?.aboutMeDetails || {};
    // The header previously showed data.client_name, which comes from the
    // originating enquiry and can differ from the resident's actual recorded
    // name. Build the real name from the personal details (first + middle +
    // surname) and fall back to the enquiry name only if those are empty.
    const fullName = [p.first_name, p.surname].filter(Boolean).join(' ').trim();
    const displayName = fullName || data?.client_name || 'Resident';

    return (
        <Modal isOpen={true} onClose={onClose} hideHeader bodyClassName="flex flex-col min-h-0" maxWidth="max-w-4xl" closeOnBackdrop>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33]">
                    <div className="flex items-center gap-4">
                        <ClientAvatar url={data?.profile_pic_url} name={displayName} className="w-12 h-12" textSize="text-lg" />
                        <div>
                            <h3 className="text-[17px] font-semibold text-gray-800 dark:text-gray-100">{displayName}</h3>
                            <p className="text-[12px] text-gray-500 dark:text-gray-400">{p.preferred_name ? `Preferred: ${p.preferred_name}` : 'Resident details'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onEdit}
                            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                        >
                            Edit
                        </button>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2c313a] text-gray-500">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="p-6 flex-1 min-h-0 overflow-y-auto">
                    <ViewSection title="Personal Information">
                        <DetailField label="Full name" value={fullName} />
                        <DetailField label="Preferred name" value={p.preferred_name} />
                        <DetailField label="NHS No" value={p.nhs_number} />
                        <DetailField label="Date of birth" value={formatDate(p.dob)} />
                        <DetailField label="Gender" value={p.gender} />
                        <DetailField label="Marital Status" value={p.marital_status} />
                        <DetailField label="First Language" value={p.first_language} />
                        <DetailField label="Ethnicity" value={p.ethnicity} />
                        <DetailField label="Nationality" value={p.nationality} />
                        <DetailField label="Religion" value={p.religion} />
                        <DetailField label="Employment Status" value={p.employment_status} />
                        <DetailField label="Date of admission" value={formatDate(p.date_of_admission)} />
                        <DetailField label="Type of stay" value={p.type_of_stay} />
                        <DetailField label="Level of care" value={p.level_of_care} />
                        <DetailField label="Room No" value={p.allocated_room_unit} />
                        <DetailField label="Unit" value={p.allocated_unit} />
                        <DetailField label="Floor Number" value={p.floor_number} />
                        <DetailField label="GP details" value={p.gp_name} />
                        <DetailField label="GP address" value={p.gp_address} />
                        <DetailField label="Current weight" value={p.current_weight} />
                        <DetailField label="Current height" value={p.current_height} />
                        <DetailField label="Mobility" value={p.mobility} />
                        <DetailField label="Continence" value={p.continence} />
                        <DetailField label="Known allergies" value={p.known_allergies} />
                        <DetailField label="Identity marks" value={p.identity_marks} />
                    </ViewSection>

                    {flags.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-3">Care Flags</h3>
                            <div className="flex flex-wrap gap-2">
                                {flags.map((flag) => (
                                    <span key={flag.id} className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">
                                        <Flag className="w-3 h-3" /> {flag.flag_type}{flag.severity ? ` · ${flag.severity}` : ''}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <ViewSection title="Address Information" cols="grid-cols-2 md:grid-cols-4">
                        <DetailField label="Address 1" value={c.address_line_1} />
                        <DetailField label="Address 2" value={c.address_line_2} />
                        <DetailField label="City" value={c.city} />
                        <DetailField label="County" value={c.county_state} />
                        <DetailField label="Post code" value={c.postcode} />
                        <DetailField label="Country" value={c.country} />
                        <DetailField label="Phone" value={c.home_phone} />
                        <DetailField label="Mobile" value={c.mobile_phone} />
                        <DetailField label="Email" value={c.personal_email} />
                    </ViewSection>

                    <ViewSection title="Next of Kin & Emergency Contacts">
                        <DetailField label="NOK Full name" value={c.nok_full_name} />
                        <DetailField label="NOK Relationship" value={c.nok_relationship} />
                        <DetailField label="NOK Contact number" value={c.nok_contact_number} />
                        <DetailField label="NOK Email" value={c.nok_email} />
                        <DetailField label="POA status" value={c.nok_poa_status} />
                        <DetailField label="Secondary contact" value={c.sec_contact_full_name} />
                        <DetailField label="Secondary relationship" value={c.sec_contact_relationship} />
                        <DetailField label="Secondary number" value={c.sec_contact_number} />
                    </ViewSection>

                    {data?.medicalDetails && (
                        <ViewSection title="Medical">
                            <DetailField label="Primary diagnosis" value={m.primary_diagnosis} />
                            <DetailField label="Secondary diagnosis" value={m.secondary_diagnosis} />
                            <DetailField label="Resuscitation status" value={m.resuscitation_status} />
                            <DetailField label="Mental capacity" value={m.mental_capacity} />
                            <DetailField label="Dietary requirements" value={m.dietary_requirements} />
                            <DetailField label="Allergies detail" value={m.allergies_detail} />
                            <DetailField label="Last medical review" value={formatDate(m.last_medical_review_date)} />
                            <DetailField label="Current medications" value={m.current_medications_overview} />
                            <DetailField label="Preferred pharmacy" value={m.preferred_pharmacy_name} />
                            <DetailField label="Recent hospital admissions" value={m.recent_hospital_admissions} />
                            <DetailField label="Dentist" value={m.dentist_name} />
                            <DetailField label="Dentist contact" value={m.dentist_contact} />
                            <DetailField label="Optician" value={m.optician_name} />
                            <DetailField label="Optician contact" value={m.optician_contact} />
                            <DetailField label="Chiropodist" value={m.chiropodist_name} />
                            <DetailField label="Chiropodist contact" value={m.chiropodist_contact} />
                        </ViewSection>
                    )}

                    {data?.financeDetails && (() => {
                        const orgType = (user?.organization?.organization_type || '').toUpperCase();
                        const isDom = orgType === 'DOMICILIARY'
                            || (data?.residence_type || '').toUpperCase() === 'DOMICILIARY';
                        return (
                            <ViewSection title="Finance">
                                {/* Funding setup */}
                                <DetailField label="Type of funding" value={f.funding_type} />
                                <DetailField label="Funding body" value={f.funding_body} />
                                <DetailField label="Source of referral" value={f.source_of_referral} />
                                <DetailField label="Type of care required" value={f.care_type_required} />
                                {/* Fee details */}
                                <DetailField label={isDom ? 'Service start date' : 'Admission date'} value={formatDate(f.admission_date)} />
                                <DetailField label="Billing start date" value={formatDate(f.billing_start_date)} />
                                <DetailField label="Fee frequency" value={f.fee_frequency} />
                                {isDom ? (
                                    <>
                                        <DetailField label="Hourly rate (£)" value={f.hourly_rate} />
                                        <DetailField label="Agreed weekly hours" value={f.care_hours_required} />
                                        <DetailField label="Estimated weekly fee (£)" value={f.agreed_amount} />
                                    </>
                                ) : (
                                    <>
                                        <DetailField label="Agreed weekly fee (£)" value={f.agreed_amount} />
                                        <DetailField label="Assessed weekly fee (£)" value={f.assessed_fee} />
                                        <DetailField label="Care dependency level" value={f.care_dependency_level} />
                                    </>
                                )}
                                {/* Finance contact */}
                                <DetailField label="Invoice contact name" value={f.invoice_contact_name} />
                                <DetailField label="Relationship" value={f.invoice_contact_relationship} />
                                <DetailField label="Invoice email" value={f.invoice_email} />
                                <DetailField label="Invoice phone" value={f.invoice_phone} />
                                <DetailField label="LPA holder for finance" value={f.lpa_holder_finance} />
                                <DetailField label="PO / reference" value={f.po_reference} />
                                {/* Payment & notes */}
                                <DetailField label="Payment method" value={f.payment_method} />
                                <DetailField label="Payment status" value={f.payment_status} />
                                <DetailField label="Funding notes" value={f.income_note} />
                            </ViewSection>
                        );
                    })()}

                    {data?.aboutMeDetails && (
                        <ViewSection title="About Me">
                            <DetailField label="Previous occupation" value={a.previous_occupation} />
                            <DetailField label="Hobbies & interests" value={a.hobbies_interests} />
                            <DetailField label="Important life events" value={a.important_life_events} />
                            <DetailField label="Spiritual / cultural needs" value={a.spiritual_cultural_needs} />
                            <DetailField label="Religious / cultural practices" value={a.religious_cultural_practices_details} />
                            <DetailField label="Usual wake-up time" value={a.usual_wake_up_time} />
                            <DetailField label="Usual bed time" value={a.usual_bed_time} />
                            <DetailField label="Food likes" value={a.food_likes} />
                            <DetailField label="Food dislikes" value={a.food_dislikes} />
                            <DetailField label="Preferred address name" value={a.preferred_address_name} />
                            <DetailField label="Distress / anxiety triggers" value={a.distress_anxiety_triggers} />
                            <DetailField label="Comfort / calming things" value={a.comfort_calming_things} />
                        </ViewSection>
                    )}
                </div>
        </Modal>
    );
};

const FLAG_TYPES = [
    'DNACPR',
    'Allergy',
    'DOLS / Capacity',
    'Mobility',
    'Diet / Swallowing',
    'Falls Risk',
    'Medication Alert',
    'Communication Need',
    'Safeguarding Alert',
    'Ability / Independence',
];

const SEVERITY_CONFIG = {
    Critical:    { dot: 'bg-red-500',    text: 'text-red-600',    badge: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' },
    Attention:   { dot: 'bg-orange-400', text: 'text-orange-500', badge: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800' },
    Legal:       { dot: 'bg-purple-500', text: 'text-purple-600', badge: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' },
    Information: { dot: 'bg-blue-500',   text: 'text-blue-600',   badge: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' },
    Positive:    { dot: 'bg-green-500',  text: 'text-green-600',  badge: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800' },
};

const DEFAULT_FLAG_FORM = {
    flag_type: '',
    severity: 'Information',
    specific_detail: '',
    review_due: '',
    notes_for_carers: '',
    is_active: true,
};


const ResidenceDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    // Deep link: `?tab=<slug>&focus=<recordId>` opens a tab, and optionally one
    // record inside it. Used by the Reminders card (utils/reminderLinks.js).
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedTab = TAB_BY_SLUG[searchParams.get('tab')] || null;
    const focusId = searchParams.get('focus');
    const [activeTab, setActiveTab] = useState(linkedTab || 'Personal details');
    // Following a second link while already on this page (e.g. a reminder on
    // the resident's own Reminders card) switches the tab too.
    useEffect(() => {
        if (linkedTab) setActiveTab(linkedTab);
    }, [linkedTab, focusId]);
    // Choosing a tab by hand drops the link, so a refresh doesn't jump back
    // to the reminder's record.
    const selectTab = (tab) => {
        setActiveTab(tab);
        if (searchParams.get('tab') || searchParams.get('focus')) setSearchParams({}, { replace: true });
    };
    // Sub-view inside the merged "Vitals & Nutrients" tab
    const [vnView, setVnView] = useState('vitals'); // 'vitals' | 'nutrients' | 'weight' | 'continence'
    const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [residenceData, setResidenceData] = useState(null);
    const [error, setError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
    const [docPreview, setDocPreview] = useState(null); // { url, name, title } shown in DocumentViewer
    const [uploadingPic, setUploadingPic] = useState(false);
    const picInputRef = useRef(null);
    // Emergency panel: open flag + the button's screen position (zoom origin)
    const [isEmergencyOpen, setIsEmergencyOpen] = useState(false);
    const [emergencyOrigin, setEmergencyOrigin] = useState(null);
    // Tab strip: scroll with arrow buttons instead of a visible scrollbar
    const tabsScrollRef = useRef(null);
    const [tabScroll, setTabScroll] = useState({ left: false, right: false });
    const [isSaving, setIsSaving] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [isFlagSidebarOpen, setIsFlagSidebarOpen] = useState(false);
    const [flagForm, setFlagForm] = useState(DEFAULT_FLAG_FORM);
    const [flags, setFlags] = useState([]);
    const [isSavingFlag, setIsSavingFlag] = useState(false);
    // Sidebar view: 'list' shows all flags, 'form' adds/edits one
    const [flagView, setFlagView] = useState('list');
    // null = adding a new flag; otherwise the id of the flag being edited
    const [editingFlagId, setEditingFlagId] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [appointmentsLoading, setAppointmentsLoading] = useState(true);
    const [isSavingAppointment, setIsSavingAppointment] = useState(false);
    // Calendar view month — {year, month}; defaults to current month
    const [calendarView, setCalendarView] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });
    const [formData, setFormData] = useState({
        reason: '',
        date: '',
        time: '',
        description: ''
    });

    // Fetch residence data on component mount
    useEffect(() => {
        const fetchResidenceData = async () => {
            try {
                setLoading(true);
                const response = await residencesService.getResidenceById(id);
                setResidenceData(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching residence:', err);
                setError('Failed to load residence details');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchResidenceData();
        }
    }, [id]);

    // Reload residence data (used after saving a sub-section like medication)
    const refreshResidence = async () => {
        try {
            const response = await residencesService.getResidenceById(id);
            setResidenceData(response.data);
        } catch (err) {
            console.error('Error refreshing residence:', err);
        }
    };

    // Fetch appointments
    const fetchAppointments = async () => {
        try {
            setAppointmentsLoading(true);
            const response = await residencesService.getResidentAppointments(id);
            setAppointments(response.data?.records || []);
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setAppointmentsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchAppointments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Sync flags from loaded residence data
    // MySQL JSON columns can come back as a string — always parse and guard
    useEffect(() => {
        const raw = residenceData?.personalDetails?.care_flags;
        if (raw == null) { setFlags([]); return; }
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            setFlags(Array.isArray(parsed) ? parsed : []);
        } catch {
            setFlags([]);
        }
    }, [residenceData]);

    const handleFlagFormChange = (e) => {
        const { name, value } = e.target;
        setFlagForm(prev => ({ ...prev, [name]: value }));
    };

    // Open the sidebar showing the list of all flags
    const openFlagsSidebar = () => {
        setFlagView('list');
        setEditingFlagId(null);
        setFlagForm(DEFAULT_FLAG_FORM);
        setIsFlagSidebarOpen(true);
    };

    // Switch the sidebar into "add new flag" mode
    const startAddFlag = () => {
        setEditingFlagId(null);
        setFlagForm(DEFAULT_FLAG_FORM);
        setFlagView('form');
    };

    // Switch the sidebar into "edit" mode for a given flag
    const startEditFlag = (flag) => {
        setEditingFlagId(flag.id);
        setFlagForm({
            flag_type: flag.flag_type || '',
            severity: flag.severity || 'Information',
            specific_detail: flag.specific_detail || '',
            review_due: flag.review_due ? flag.review_due.split('T')[0] : '',
            notes_for_carers: flag.notes_for_carers || '',
            is_active: flag.is_active !== false,
        });
        setFlagView('form');
    };

    const closeFlagSidebar = () => {
        setIsFlagSidebarOpen(false);
        setFlagView('list');
        setEditingFlagId(null);
        setFlagForm(DEFAULT_FLAG_FORM);
    };

    // Persist the flags array to the backend and update local state
    const persistFlags = async (updatedFlags) => {
        await residencesService.updateResidence(id, {
            personalDetails: { care_flags: updatedFlags }
        });
        setFlags(updatedFlags);
    };

    const handleSaveFlag = async () => {
        if (!flagForm.flag_type) return;
        try {
            setIsSavingFlag(true);
            let updatedFlags;
            if (editingFlagId) {
                // Update the existing flag, preserving id/created_at
                updatedFlags = flags.map(f =>
                    f.id === editingFlagId ? { ...f, ...flagForm } : f
                );
            } else {
                // Add a brand new flag
                const newFlag = {
                    ...flagForm,
                    id: Date.now().toString(),
                    created_at: new Date().toISOString(),
                };
                updatedFlags = [...flags, newFlag];
            }
            await persistFlags(updatedFlags);
            // Return to the list view so the user sees the change
            setFlagView('list');
            setEditingFlagId(null);
            setFlagForm(DEFAULT_FLAG_FORM);
        } catch (err) {
            console.error('Error saving flag:', err);
        } finally {
            setIsSavingFlag(false);
        }
    };

    const handleDeleteFlag = async (flagId) => {
        try {
            const updatedFlags = flags.filter(f => f.id !== flagId);
            await persistFlags(updatedFlags);
        } catch (err) {
            console.error('Error deleting flag:', err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddAppointment = async (e) => {
        e.preventDefault();
        try {
            setIsSavingAppointment(true);
            await residencesService.createAppointment(id, {
                title: formData.reason,
                date: formData.date,
                time: formData.time,
                about: formData.description,
            });
            await fetchAppointments();
            setFormData({ reason: '', date: '', time: '', description: '' });
            setIsAppointmentModalOpen(false);
        } catch (err) {
            console.error('Error adding appointment:', err);
        } finally {
            setIsSavingAppointment(false);
        }
    };

    const handleDeleteAppointment = async (appointmentId) => {
        try {
            await residencesService.deleteAppointment(id, appointmentId);
            setAppointments(prev => prev.filter(a => a.id !== appointmentId));
        } catch (err) {
            console.error('Error deleting appointment:', err);
        }
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    // Handle Update Button Click - Enable Edit Mode
    const handleUpdateClick = () => {
        setIsEditMode(true);
        // Initialize edit form data from current residence data
        setEditFormData({
            personalDetail_nhs_number: residenceData?.personalDetails?.nhs_number || '',
            personalDetail_dob: residenceData?.personalDetails?.dob || '',
            personalDetail_gender: residenceData?.personalDetails?.gender || '',
            personalDetail_allocated_room_unit: residenceData?.personalDetails?.allocated_room_unit || '',
            personalDetail_allocated_unit: residenceData?.personalDetails?.allocated_unit || '',
            personalDetail_floor_number: residenceData?.personalDetails?.floor_number || '',
            personalDetail_preferred_name: residenceData?.personalDetails?.preferred_name || '',
            personalDetail_gp_name: residenceData?.personalDetails?.gp_name || '',
            personalDetail_first_language: residenceData?.personalDetails?.first_language || '',
            personalDetail_marital_status: residenceData?.personalDetails?.marital_status || '',
            contact_address_line_1: residenceData?.contactDetails?.address_line_1 || '',
            contact_address_line_2: residenceData?.contactDetails?.address_line_2 || '',
            contact_city: residenceData?.contactDetails?.city || '',
            contact_county_state: residenceData?.contactDetails?.county_state || '',
            contact_postcode: residenceData?.contactDetails?.postcode || '',
            contact_country: residenceData?.contactDetails?.country || '',
            contact_latitude: residenceData?.contactDetails?.latitude ?? null,
            contact_longitude: residenceData?.contactDetails?.longitude ?? null,
            contact_home_phone: residenceData?.contactDetails?.home_phone || '',
            contact_mobile_phone: residenceData?.contactDetails?.mobile_phone || '',
            contact_nok_full_name: residenceData?.contactDetails?.nok_full_name || '',
            contact_nok_contact_number: residenceData?.contactDetails?.nok_contact_number || '',
            contact_nok_relationship: residenceData?.contactDetails?.nok_relationship || '',
            contact_nok_poa_status: residenceData?.contactDetails?.nok_poa_status || '',
            contact_sec_contact_full_name: residenceData?.contactDetails?.sec_contact_full_name || '',
            contact_sec_contact_number: residenceData?.contactDetails?.sec_contact_number || '',
            contact_sec_contact_relationship: residenceData?.contactDetails?.sec_contact_relationship || '',
        });
    };

    // Handle Save Update
    const handleSaveUpdate = async () => {
        try {
            setIsSaving(true);
            
            // Format data for API
            const personalDetails = {
                nhs_number: editFormData.personalDetail_nhs_number,
                dob: editFormData.personalDetail_dob,
                gender: editFormData.personalDetail_gender,
                allocated_room_unit: editFormData.personalDetail_allocated_room_unit,
                allocated_unit: editFormData.personalDetail_allocated_unit,
                floor_number: editFormData.personalDetail_floor_number,
                preferred_name: editFormData.personalDetail_preferred_name,
                gp_name: editFormData.personalDetail_gp_name,
                first_language: editFormData.personalDetail_first_language,
                marital_status: editFormData.personalDetail_marital_status,
            };

            const contactDetails = {
                address_line_1: editFormData.contact_address_line_1,
                address_line_2: editFormData.contact_address_line_2,
                city: editFormData.contact_city,
                county_state: editFormData.contact_county_state,
                postcode: editFormData.contact_postcode,
                country: editFormData.contact_country,
                // Sent only when a Places suggestion supplied them; otherwise the
                // backend re-geocodes the address text after saving.
                ...(editFormData.contact_latitude != null && editFormData.contact_longitude != null && {
                    latitude: editFormData.contact_latitude,
                    longitude: editFormData.contact_longitude,
                }),
                home_phone: editFormData.contact_home_phone,
                mobile_phone: editFormData.contact_mobile_phone,
                nok_full_name: editFormData.contact_nok_full_name,
                nok_contact_number: editFormData.contact_nok_contact_number,
                nok_relationship: editFormData.contact_nok_relationship,
                nok_poa_status: editFormData.contact_nok_poa_status,
                sec_contact_full_name: editFormData.contact_sec_contact_full_name,
                sec_contact_number: editFormData.contact_sec_contact_number,
                sec_contact_relationship: editFormData.contact_sec_contact_relationship,
            };

            // Call API to update (you may need to create this endpoint in your controller)
            await residencesService.updateResidence(id, {
                personalDetails,
                contactDetails
            });

            // Refresh data
            const response = await residencesService.getResidenceById(id);
            setResidenceData(response.data);

            // Exit edit mode
            setIsEditMode(false);
            setEditFormData({});
            setError(null);
        } catch (err) {
            console.error('Error updating residence:', err);
            setError('Failed to update residence details');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle Cancel Update
    const handleCancelUpdate = () => {
        setIsEditMode(false);
        setEditFormData({});
    };

    // Handle Edit Form Change
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => {
            const next = { ...prev, [name]: value };
            // Hand-editing any part of the address invalidates the stored map
            // pin; clearing it makes the backend re-geocode from the new text.
            if (ADDRESS_FIELDS.includes(name)) {
                next.contact_latitude = null;
                next.contact_longitude = null;
            }
            return next;
        });
    };

    // A Places suggestion was chosen on the Address 1 box — fill in whatever
    // Google resolved, including the lat/lng carer distances are measured from.
    const handleAddressResolved = useCallback((fields) => {
        setEditFormData(prev => ({
            ...prev,
            ...(fields.address_line_1 !== undefined && { contact_address_line_1: fields.address_line_1 }),
            ...(fields.address_line_2 !== undefined && { contact_address_line_2: fields.address_line_2 }),
            ...(fields.city !== undefined && { contact_city: fields.city }),
            ...(fields.county_state !== undefined && { contact_county_state: fields.county_state }),
            ...(fields.postcode !== undefined && { contact_postcode: fields.postcode }),
            ...(fields.country !== undefined && { contact_country: fields.country }),
            contact_latitude: fields.latitude ?? null,
            contact_longitude: fields.longitude ?? null,
        }));
    }, []);

    const tabs = [
        'Personal details', 'Medication', 'Care notes', 'Care plan', 'Health & Nutrition', 'Behaviour', 'Risk Assessment',
        'Accident & Incident', 'Body map'
    ];

    // Show/hide the tab arrows based on how far the strip is scrolled.
    const updateTabScroll = useCallback(() => {
        const el = tabsScrollRef.current;
        if (!el) return;
        const left = el.scrollLeft > 4;
        const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
        setTabScroll(prev => (prev.left === left && prev.right === right ? prev : { left, right }));
    }, []);

    const scrollTabs = (dir) => {
        const el = tabsScrollRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * Math.max(180, el.clientWidth * 0.6), behavior: 'smooth' });
    };

    // Recompute arrow visibility on mount, on resize, and when content loads.
    useEffect(() => {
        updateTabScroll();
        window.addEventListener('resize', updateTabScroll);
        return () => window.removeEventListener('resize', updateTabScroll);
    }, [updateTabScroll, residenceData]);

    // Reusable Appointment Card Component
    const AppointmentCard = ({ title, description, borderColor, onDelete }) => (
        <div className={`bg-white dark:bg-[#262b33] rounded-xl p-4 pr-10 border border-gray-100 dark:border-gray-800 shadow-sm border-l-[4px] ${borderColor} relative min-h-[76px]`}>
            <h4 className="text-[13px] font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug mt-1.5 w-[95%]">
                {description}
            </p>
            <button
                onClick={onDelete}
                className="absolute bottom-3 right-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition p-1"
            >
                <Trash2 className="w-[14px] h-[14px]" />
            </button>
        </div>
    );

    // Status -> left border color for appointment cards
    const appointmentBorderColor = (apt) => {
        if (apt.status === 'Cancelled') return 'border-l-red-500';
        if (apt.status === 'Pending') return 'border-l-orange-400';
        const isPast = apt.appointment_date && new Date(apt.appointment_date) < new Date();
        return isPast ? 'border-l-green-500' : 'border-l-blue-500';
    };

    const formatAppointmentTitle = (apt) => {
        if (!apt.appointment_date) return apt.title;
        const d = new Date(apt.appointment_date);
        const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
        const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        return `${apt.title} - ${datePart}, ${timePart}`;
    };

    // Calendar month navigation
    const goToPrevMonth = () => setCalendarView(v => {
        const d = new Date(v.year, v.month - 1, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });
    const goToNextMonth = () => setCalendarView(v => {
        const d = new Date(v.year, v.month + 1, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });
    const goToToday = () => {
        const now = new Date();
        setCalendarView({ year: now.getFullYear(), month: now.getMonth() });
    };

    // Build a calendar grid for the month currently in view
    const buildCalendar = () => {
        const today = new Date();
        const { year, month } = calendarView;
        const anchor = new Date(year, month, 1);

        const firstDay = new Date(year, month, 1);
        // Monday-first index (0 = Mon ... 6 = Sun)
        const startOffset = (firstDay.getDay() + 6) % 7;
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        // Map appointment day-of-month -> status (only for this month/year)
        const dayStatus = {};
        appointments.forEach(a => {
            if (!a.appointment_date) return;
            const d = new Date(a.appointment_date);
            if (d.getFullYear() === year && d.getMonth() === month) {
                const day = d.getDate();
                const isPast = d < today;
                // upcoming takes priority over completed if both exist on same day
                if (dayStatus[day] !== 'upcoming') dayStatus[day] = isPast ? 'completed' : 'upcoming';
            }
        });

        const cells = [];
        for (let i = 0; i < startOffset; i++) cells.push(null);
        for (let day = 1; day <= daysInMonth; day++) cells.push(day);

        return {
            cells,
            dayStatus,
            label: anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
            todayDate: (today.getFullYear() === year && today.getMonth() === month) ? today.getDate() : null,
        };
    };

    // Real recorded name (first + middle + surname). client_name is the
    // enquiry-time name and must NOT be shown as the resident's name.
    const pd = residenceData?.personalDetails || {};
    const realName = [pd.first_name, pd.surname].filter(Boolean).join(' ').trim();
    const displayName = residenceData ? (realName || '—') : 'Loading...';

    // Upload / replace the client's profile picture.
    const handleProfilePicChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setUploadingPic(true);
            const res = await residencesService.uploadProfilePic(id, file);
            if (res.success) {
                setResidenceData((prev) => ({ ...prev, profile_pic_url: res.data.profile_pic_url }));
                toast.success('Profile picture updated');
            } else {
                toast.error(res.message || 'Upload failed');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Upload failed');
        } finally {
            setUploadingPic(false);
            if (picInputRef.current) picInputRef.current.value = '';
        }
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6 pb-8">

            {/* Left Column: Main Details & Tabs */}
            <div className="flex-1 min-w-0">

                {/* ── Header card: name + code · emergency · flags · last updated ── */}
                <div className="bg-white dark:bg-[#262b33] rounded-2xl border border-[#E5EAF3] dark:border-gray-800 shadow-sm px-5 py-4 mb-4">
                    {/* Back to the client list */}
                    <button
                        onClick={() => navigate('/manager/residences')}
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 mb-3 transition"
                    >
                        <ChevronLeft className="w-4 h-4" /> Back
                    </button>
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-wrap">
                            <h1 className="text-[22px] font-bold text-gray-800 dark:text-gray-100 tracking-tight">
                                {displayName}
                            </h1>
                            {residenceData?.residence_code && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 dark:bg-[#31373f] text-[12px] font-bold text-gray-500 dark:text-gray-300 tracking-wide">
                                    {residenceData.residence_code}
                                </span>
                            )}
                        </div>

                        {/* Emergency Info — opens the full-screen emergency panel */}
                        <button
                            type="button"
                            disabled={!residenceData}
                            onClick={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setEmergencyOrigin({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
                                setIsEmergencyOpen(true);
                            }}
                            className="flex items-center gap-2 bg-gradient-to-b from-[#EF4444] to-[#DC2626] text-white px-4 py-2.5 rounded-xl text-[13px] font-bold shadow-sm shadow-red-500/30 hover:from-[#DC2626] hover:to-[#B91C1C] transition shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <AlertTriangle className="w-4 h-4" /> Emergency Info
                        </button>
                    </div>

                    {/* Active care flags — coloured dot + label; click to manage/edit */}
                    {flags.filter(f => f.is_active).length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
                            {flags.filter(f => f.is_active).map(flag => {
                                const st = flagTypeStyle(flag.flag_type, isDark);
                                return (
                                    <button
                                        key={flag.id}
                                        type="button"
                                        onClick={() => { setIsFlagSidebarOpen(true); startEditFlag(flag); }}
                                        title={`${flag.flag_type}${flag.severity ? ' · ' + flag.severity : ''}${flag.specific_detail ? ' · ' + flag.specific_detail : ''} — click to edit`}
                                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold transition hover:opacity-70"
                                        style={{ color: st.color }}
                                    >
                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: st.color }} />
                                        {st.short}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-2.5">
                        Last updated on {formatDate(residenceData?.updated_at || residenceData?.created_at)}
                    </p>
                </div>

                {/* ── Tab menu card (arrow-scrolled, no visible scrollbar) ── */}
                <div className="relative bg-white dark:bg-[#262b33] rounded-2xl border border-[#E5EAF3] dark:border-gray-800 shadow-sm mb-4">
                    {/* Left arrow + fade */}
                    {tabScroll.left && (
                        <>
                            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-14 rounded-l-2xl bg-gradient-to-r from-white dark:from-[#262b33] to-transparent z-10" />
                            <button
                                type="button"
                                onClick={() => scrollTabs(-1)}
                                aria-label="Scroll tabs left"
                                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-[#2c313a] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-[#383e47] hover:text-blue-600 transition"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                        </>
                    )}

                    <div
                        ref={tabsScrollRef}
                        onScroll={updateTabScroll}
                        className="flex items-stretch overflow-x-auto hide-scrollbar px-2 sm:px-3"
                    >
                        {tabs.map((tab) => {
                            const active = activeTab === tab;
                            return (
                                <button
                                    key={tab}
                                    onClick={() => selectTab(tab)}
                                    className={`group relative px-3.5 py-4 text-[13.5px] whitespace-nowrap transition-colors duration-150 ${active
                                        ? 'text-blue-600 font-semibold'
                                        : 'text-gray-500 dark:text-gray-400 font-medium hover:text-gray-800 dark:hover:text-gray-200'
                                        }`}
                                >
                                    <span className={`inline-block rounded-md px-1 transition-colors ${active ? '' : 'group-hover:bg-gray-50 dark:group-hover:bg-[#2c313a]'}`}>
                                        {tab}
                                    </span>
                                    {/* Animated active underline, flush with the card's bottom edge */}
                                    <span
                                        className={`pointer-events-none absolute left-2.5 right-2.5 bottom-0 h-[3px] rounded-t-full bg-blue-600 origin-center transition-all duration-200 ${active ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-50'}`}
                                    />
                                </button>
                            );
                        })}
                    </div>

                    {/* Right arrow + fade */}
                    {tabScroll.right && (
                        <>
                            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-14 rounded-r-2xl bg-gradient-to-l from-white dark:from-[#262b33] to-transparent z-10" />
                            <button
                                type="button"
                                onClick={() => scrollTabs(1)}
                                aria-label="Scroll tabs right"
                                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-[#2c313a] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-[#383e47] hover:text-blue-600 transition"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </div>

                {/* --- CONDITIONAL TAB CONTENT --- */}

                {/* 1. Personal Details Tab */}
                {activeTab === 'Personal details' && (
                    <div className="bg-white dark:bg-[#262b33] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 mt-6 animate-in fade-in duration-300">
                        {loading ? (
                                <TabSkeleton />
                        ) : error ? (
                            <div className="text-center py-12">
                                <p className="text-red-500 text-sm">{error}</p>
                            </div>
                        ) : residenceData ? (
                            <>
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-[18px] font-semibold text-gray-800 dark:text-gray-100">Personal details</h2>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setIsDocsModalOpen(true)}
                                            className="px-5 py-1.5 flex items-center gap-1.5 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                                            title="View uploaded documents"
                                        >
                                            <FileText className="w-4 h-4" /> Documents
                                        </button>
                                        <button
                                            onClick={() => setIsViewModalOpen(true)}
                                            className="px-5 py-1.5 flex items-center gap-1.5 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                                        >
                                            <Eye className="w-4 h-4" /> More details
                                        </button>
                                        <button
                                            onClick={() => navigate(`/manager/residences/${id}/edit`)}
                                            className="px-5 py-1.5 flex items-center gap-1.5 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                                        >
                                            <Pencil className="w-4 h-4" /> Edit
                                        </button>
                                    </div>
                                </div>

                                {/* Section: Personal Information */}
                                <div className="mb-6">
                                    <h3 className="text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-3">Personal Information</h3>
                                    <div className="bg-white dark:bg-[#31373f] border border-[#E5EAF3] dark:border-gray-700 rounded-lg p-5 flex flex-col md:flex-row gap-6">
                                        {/* Photo + change control */}
                                        <div className="flex-shrink-0">
                                            <div className="relative w-[100px] h-[100px]">
                                                <ClientAvatar url={residenceData?.profile_pic_url} name={displayName} className="w-[100px] h-[100px]" textSize="text-3xl" />
                                                <button
                                                    type="button"
                                                    onClick={() => picInputRef.current?.click()}
                                                    disabled={uploadingPic}
                                                    className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center shadow hover:bg-blue-700 transition disabled:opacity-60"
                                                    title="Change photo"
                                                >
                                                    <Camera className="w-4 h-4" />
                                                </button>
                                                <input ref={picInputRef} type="file" accept="image/*" className="hidden" onChange={handleProfilePicChange} />
                                            </div>
                                            {uploadingPic && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 text-center">Uploading…</p>}
                                        </div>
                                        {/* Grid */}
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-4 w-full">
                                            <DetailField label="First name" value={residenceData?.personalDetails?.first_name} />
                                            <DetailField label="Middle name" value={residenceData?.personalDetails?.middle_name} />
                                            <DetailField label="Surname" value={residenceData?.personalDetails?.surname} />
                                            <EditableField label="NHS No" value={residenceData?.personalDetails?.nhs_number} fieldName="personalDetail_nhs_number" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Date of birth" value={residenceData?.personalDetails?.dob} fieldName="personalDetail_dob" type="date" isDateField={true} isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Gender" value={residenceData?.personalDetails?.gender} fieldName="personalDetail_gender" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Floor Number" value={residenceData?.personalDetails?.floor_number} fieldName="personalDetail_floor_number" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Preferred name" value={residenceData?.personalDetails?.preferred_name} fieldName="personalDetail_preferred_name" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Room No" value={residenceData?.personalDetails?.allocated_room_unit} fieldName="personalDetail_allocated_room_unit" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Unit" value={residenceData?.personalDetails?.allocated_unit} fieldName="personalDetail_allocated_unit" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="GP details" value={residenceData?.personalDetails?.gp_name} fieldName="personalDetail_gp_name" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="First Language" value={residenceData?.personalDetails?.first_language} fieldName="personalDetail_first_language" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Marital Status" value={residenceData?.personalDetails?.marital_status} fieldName="personalDetail_marital_status" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Address Information */}
                                <div className="mb-6">
                                    <h3 className="text-[15px] font-semibold text-gray-700 dark:text-gray-300 mb-3">Address Information</h3>
                                    <div className="bg-white dark:bg-[#31373f] border border-[#E5EAF3] dark:border-gray-700 rounded-lg p-5">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-5 gap-x-4">
                                            {isEditMode ? (
                                                <AddressAutocomplete
                                                    variant="inline"
                                                    label="Address 1"
                                                    name="contact_address_line_1"
                                                    value={editFormData.contact_address_line_1 || ''}
                                                    onChange={handleEditFormChange}
                                                    onResolve={handleAddressResolved}
                                                />
                                            ) : (
                                                <EditableField label="Address 1" value={residenceData?.contactDetails?.address_line_1} fieldName="contact_address_line_1" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            )}
                                            <EditableField label="Address 2" value={residenceData?.contactDetails?.address_line_2} fieldName="contact_address_line_2" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="City" value={residenceData?.contactDetails?.city} fieldName="contact_city" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="County" value={residenceData?.contactDetails?.county_state} fieldName="contact_county_state" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Post code" value={residenceData?.contactDetails?.postcode} fieldName="contact_postcode" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Country" value={residenceData?.contactDetails?.country} fieldName="contact_country" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Phone" value={residenceData?.contactDetails?.home_phone} fieldName="contact_home_phone" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Mobile" value={residenceData?.contactDetails?.mobile_phone} fieldName="contact_mobile_phone" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Contact Information */}
                                <div>
                                    <h3 className="text-[15px] font-semibold text-gray-700 dark:text-gray-300 mb-3">Contact Information</h3>
                                    <div className="bg-white dark:bg-[#31373f] border border-[#E5EAF3] dark:border-gray-700 rounded-lg p-5">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4">
                                            <EditableField label="First Contact Name" value={residenceData?.contactDetails?.nok_full_name} fieldName="contact_nok_full_name" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="First contact Number" value={residenceData?.contactDetails?.nok_contact_number} fieldName="contact_nok_contact_number" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Relation with resident" value={residenceData?.contactDetails?.nok_relationship} fieldName="contact_nok_relationship" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Power of attorney" value={residenceData?.contactDetails?.nok_poa_status} fieldName="contact_nok_poa_status" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />

                                            <EditableField label="Second Contact Name" value={residenceData?.contactDetails?.sec_contact_full_name} fieldName="contact_sec_contact_full_name" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Second contact Number" value={residenceData?.contactDetails?.sec_contact_number} fieldName="contact_sec_contact_number" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <EditableField label="Relation with resident" value={residenceData?.contactDetails?.sec_contact_relationship} fieldName="contact_sec_contact_relationship" isEditMode={isEditMode} editFormData={editFormData} handleEditFormChange={handleEditFormChange} />
                                            <DetailField label="Power of attorney" value="N/A" />
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Care Flags */}
                                <div className="mt-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <h3 className="text-[15px] font-semibold text-gray-700 dark:text-gray-300">Care flags and clinical alerts</h3>
                                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Important alerts shown on the dashboard, rota, resident profile, and carer app.</p>
                                        </div>
                                        <button
                                            onClick={openFlagsSidebar}
                                            className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-blue-700 transition flex-shrink-0 ml-4"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Manage flags
                                        </button>
                                    </div>

                                    {flags.filter(f => f.is_active).length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                            {flags.filter(f => f.is_active).map(flag => {
                                                const st = flagTypeStyle(flag.flag_type, isDark);
                                                const reviewDate = flag.review_due
                                                    ? new Date(flag.review_due).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                                    : '-';
                                                return (
                                                    <div
                                                        key={flag.id}
                                                        onClick={() => { setIsFlagSidebarOpen(true); startEditFlag(flag); }}
                                                        className="bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex flex-col gap-2 relative cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition"
                                                        title="Click to edit this flag"
                                                    >
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteFlag(flag.id); }}
                                                            className="absolute top-3 right-3 text-gray-300 dark:text-gray-600 hover:text-red-400 transition"
                                                            title="Remove flag"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: st.color }} />
                                                            <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-100 pr-5">{flag.flag_type}</span>
                                                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: st.bg, color: st.color }}>{st.short}</span>
                                                        </div>
                                                        <p className="text-[12px] text-gray-500 dark:text-gray-400 ml-[18px] leading-snug">{flag.specific_detail || '-'}</p>
                                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                                                            <span className="text-[11px] text-gray-400 dark:text-gray-500">Review due</span>
                                                            <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300">{reviewDate}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 bg-gray-50 dark:bg-[#31373f] rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                                            <p className="text-[13px] text-gray-400 dark:text-gray-500">No active flags. Click "Manage flags" to add one.</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-gray-500 dark:text-gray-400 text-sm">No residence data available</p>
                            </div>
                        )}

                    </div>
                )}

                {isViewModalOpen && residenceData && (
                    <ResidentDetailsViewModal
                        data={residenceData}
                        flags={flags}
                        formatDate={formatDate}
                        onClose={() => setIsViewModalOpen(false)}
                        onEdit={() => { setIsViewModalOpen(false); navigate(`/manager/residences/${id}/edit`); }}
                    />
                )}

                {/* Documents uploaded at client creation */}
                <Modal isOpen={isDocsModalOpen} onClose={() => setIsDocsModalOpen(false)} title="Client documents" maxWidth="max-w-[560px]">
                    <ClientDocuments docs={residenceData?.documentations || {}} onOpen={setDocPreview} />
                </Modal>
                {/* Opens over the list; closing it goes back to the list. */}
                <DocumentViewer file={docPreview} onClose={() => setDocPreview(null)} />

                {isEmergencyOpen && residenceData && (
                    <EmergencyInfoModal
                        residenceId={id}
                        residentName={realName || residenceData?.client_name}
                        origin={emergencyOrigin}
                        onClose={() => setIsEmergencyOpen(false)}
                    />
                )}

                {/* Medication Tab */}
                {activeTab === 'Medication' && (
                    <MedicationTab
                        residenceId={id}
                        medical={residenceData?.medicalDetails || {}}
                        onSaved={refreshResidence}
                    />
                )}

                {/* 2. Care Plan Tab */}
                {activeTab === 'Care plan' && (
                    <CareNeedsTab residenceId={id} residentName={residenceData?.client_name || ''} focusId={focusId} />
                )}

                {activeTab === 'Risk Assessment' && (
                    <RiskAssessmentTab residenceId={id} focusId={focusId} />
                )}

                {activeTab === 'Body map' && (
                    <BodyMapTab />
                )}

                {activeTab === 'Accident & Incident' && (
                    <AccidentIncidentTab focusId={focusId} />
                )}
                {activeTab === 'Care notes' && (
                    <CareNotesTab residenceId={id} />
                )}
                {activeTab === 'Health & Nutrition' && (
                    <div className="mt-6">
                        <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mb-5">
                            {[['vitals', 'Vitals'], ['nutrients', 'Nutrients'], ['weight', 'Weight'], ['continence', 'Continence']].map(([v, label]) => (
                                <button key={v} onClick={() => setVnView(v)}
                                    className={`px-5 py-1.5 rounded-md text-[13px] font-medium transition ${vnView === v ? 'bg-white dark:bg-[#262b33] text-blue-600 shadow-sm' : 'text-gray-500'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                        {/* Distinct keys force React to remount VitalsTab when
                            switching between Vitals and Weight — otherwise the
                            same instance is reused and its vitalsType state stays
                            stale, so Weight data wouldn't load on first switch. */}
                        {vnView === 'vitals'
                            ? <VitalsTab key="vitals" />
                            : vnView === 'nutrients'
                                ? <NutrientsTab />
                                : vnView === 'continence'
                                    ? <ContinenceTab />
                                    : <VitalsTab key="weight" fixedType="Weight" />}
                    </div>
                )}
                {activeTab === 'Behaviour' && (
                    <BehaviourTab />
                )}

                {[].includes(activeTab) && (
                    <div className="bg-white dark:bg-[#262b33] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 mt-6 text-center animate-in fade-in duration-300">
                        <p className="text-gray-500 dark:text-gray-400 font-medium">{activeTab} content coming soon...</p>
                    </div>
                )}

            </div>

            {/* Right Column: Appointments & Sidebar */}
            <div className="w-full lg:w-[320px] xl:w-[340px] flex-shrink-0 mt-2 lg:mt-0">

                {/* Calendar Card */}
                {(() => {
                    const cal = buildCalendar();
                    return (
                <div className="bg-white dark:bg-[#262b33] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100">Calendar</h3>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={goToPrevMonth}
                                className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#31373f] transition"
                                aria-label="Previous month"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={goToToday}
                                className="text-[12px] font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition min-w-[100px] text-center"
                                title="Jump to current month"
                            >
                                {cal.label}
                            </button>
                            <button
                                onClick={goToNextMonth}
                                className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#31373f] transition"
                                aria-label="Next month"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center mb-4">
                        {['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'].map(day => (
                            <div key={day} className="text-[10px] text-gray-400 dark:text-gray-500 font-medium mb-2">{day}</div>
                        ))}
                        {cal.cells.map((date, idx) => {
                            if (date === null) return <div key={`e-${idx}`} />;
                            const hasAppointment = !!cal.dayStatus[date];
                            const isToday = cal.todayDate === date;
                            return (
                                <div key={date} className="flex flex-col items-center justify-start mx-auto pt-1">
                                    <div
                                        className={`text-[12px] font-medium w-[26px] h-[26px] flex items-center justify-center rounded-full transition
                  ${isToday ? 'ring-[1.5px] ring-blue-500 text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-600 dark:text-gray-400 bg-[#F5F9FF] dark:bg-transparent hover:bg-gray-100 dark:hover:bg-[#31373f]'}
                `}
                                    >
                                        {date}
                                    </div>
                                    <span className={`mt-1 w-1.5 h-1.5 rounded-full ${hasAppointment ? 'bg-blue-600' : 'bg-transparent'}`} />
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex justify-center gap-5 mt-2">
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span>
                            <span className="text-[11px] text-gray-600 dark:text-gray-400 font-medium">Appointment</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full border-[1.5px] border-blue-500"></span>
                            <span className="text-[11px] text-gray-600 dark:text-gray-400 font-medium">Today</span>
                        </div>
                    </div>
                </div>
                    );
                })()}

                {/* Appointments List */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100">Appointments</h3>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsAppointmentModalOpen(true)}
                                className="text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition"
                            >
                                Add
                            </button>
                            <button className="text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:underline">See all</button>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {appointmentsLoading ? (
                            [1, 2, 3].map(i => (
                                <div key={i} className="h-[76px] bg-gray-50 dark:bg-[#262b33] animate-pulse rounded-xl" />
                            ))
                        ) : appointments.length > 0 ? (
                            appointments.map((apt) => (
                                <AppointmentCard
                                    key={apt.id}
                                    title={formatAppointmentTitle(apt)}
                                    description={apt.about || apt.professional_name || 'No additional details.'}
                                    borderColor={appointmentBorderColor(apt)}
                                    onDelete={() => handleDeleteAppointment(apt.id)}
                                />
                            ))
                        ) : (
                            <div className="py-8 text-center text-[13px] text-gray-500 dark:text-gray-400 bg-white dark:bg-[#262b33] rounded-xl border border-dashed border-gray-200 dark:border-gray-800">
                                No appointments booked yet.
                            </div>
                        )}
                    </div>
                </div>

                {/* Reminders (review / follow-up due for this resident) */}
                <div className="mt-6">
                    <RemindersCard residenceId={id} />
                </div>

            </div>

            {/* Manage Flags Sidebar */}
            <DetailsSidebar
                isOpen={isFlagSidebarOpen}
                onClose={closeFlagSidebar}
                title={flagView === 'list' ? 'Manage flags' : editingFlagId ? 'Edit flag' : 'Add new flag'}
                icon={Flag}
                maxWidth="max-w-[440px]"
                footer={
                    flagView === 'list' ? (
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={closeFlagSidebar}
                                className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition text-sm"
                            >
                                Close
                            </button>
                            <button
                                onClick={startAddFlag}
                                className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition text-sm flex items-center justify-center gap-1.5"
                            >
                                <Plus className="w-4 h-4" />
                                Add new flag
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => { setFlagView('list'); setEditingFlagId(null); setFlagForm(DEFAULT_FLAG_FORM); }}
                                className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveFlag}
                                disabled={isSavingFlag || !flagForm.flag_type}
                                className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSavingFlag ? 'Saving...' : editingFlagId ? 'Update flag' : 'Save flag'}
                            </button>
                        </div>
                    )
                }
            >
                {flagView === 'list' ? (
                    /* ----- LIST VIEW: all flags, click to edit ----- */
                    <div className="space-y-4">
                        <p className="text-[12px] text-gray-400 dark:text-gray-500 -mt-2">Manager / admin only · {residenceData?.client_name}</p>
                        {flags.length > 0 ? (
                            <div className="space-y-3">
                                {flags.map(flag => {
                                    const st = flagTypeStyle(flag.flag_type, isDark);
                                    const sev = SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.Information;
                                    return (
                                        <div
                                            key={flag.id}
                                            onClick={() => startEditFlag(flag)}
                                            className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-start gap-3 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition group"
                                            title="Click to edit"
                                        >
                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: st.color }} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">{flag.flag_type}</span>
                                                    {!flag.is_active && (
                                                        <span className="text-[10px] font-medium text-gray-400 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5">Inactive</span>
                                                    )}
                                                </div>
                                                <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">{flag.specific_detail || 'No detail'}</p>
                                                <span className={`inline-block text-[11px] font-medium mt-1 ${sev.text}`}>{flag.severity}</span>
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteFlag(flag.id); }}
                                                className="text-gray-300 dark:text-gray-600 hover:text-red-400 transition p-1 flex-shrink-0"
                                                title="Remove flag"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-10 bg-gray-50 dark:bg-[#31373f] rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                                <p className="text-[13px] text-gray-400 dark:text-gray-500">No flags yet. Click "Add new flag" below.</p>
                            </div>
                        )}
                    </div>
                ) : (
                /* ----- FORM VIEW: add or edit a flag ----- */
                <div className="space-y-5">
                    <p className="text-[12px] text-gray-400 dark:text-gray-500 -mt-2">Manager / admin only · {residenceData?.client_name}</p>

                    {/* Flag Type */}
                    <div>
                        <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Flag type</label>
                        <Select
                            value={flagForm.flag_type}
                            onChange={(v) => setFlagForm((prev) => ({ ...prev, flag_type: v }))}
                            placeholder="Choose flag type..."
                            options={FLAG_TYPES.map((t) => ({ value: t, label: t }))}
                            className="w-full"
                        />
                        {/* Colour preview — every flag type has its own colour & short tag */}
                        {flagForm.flag_type && (() => {
                            const st = flagTypeStyle(flagForm.flag_type, isDark);
                            return (
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-[11px] text-gray-400 dark:text-gray-500">Shows as</span>
                                    <span
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold"
                                        style={{ background: st.bg, color: st.color }}
                                    >
                                        <span className="w-2 h-2 rounded-full" style={{ background: st.color }} />
                                        {st.short}
                                    </span>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Severity */}
                    <div>
                        <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-2">Severity</label>
                        <div className="flex gap-2 flex-wrap">
                            {Object.keys(SEVERITY_CONFIG).map(s => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setFlagForm(p => ({ ...p, severity: s }))}
                                    className={`px-3.5 py-1.5 rounded-lg border text-[12px] font-medium transition ${
                                        flagForm.severity === s
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#3a4048]'
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Specific Detail */}
                    <div>
                        <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Specific detail</label>
                        <input
                            type="text"
                            name="specific_detail"
                            value={flagForm.specific_detail}
                            onChange={handleFlagFormChange}
                            placeholder="e.g. Penicillin allergy"
                            className="w-full px-3 py-2.5 bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-lg text-[13px] text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                    </div>

                    {/* Review Due Date */}
                    <div>
                        <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Review due date</label>
                        <input
                            type="date"
                            name="review_due"
                            value={flagForm.review_due}
                            onChange={handleFlagFormChange}
                            className="w-full px-3 py-2.5 bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-lg text-[13px] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                    </div>

                    {/* Notes for carers */}
                    <div>
                        <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Notes for carers</label>
                        <textarea
                            name="notes_for_carers"
                            value={flagForm.notes_for_carers}
                            onChange={handleFlagFormChange}
                            rows={4}
                            placeholder="Short context, instructions, escalation path..."
                            className="w-full px-3 py-2.5 bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-lg text-[13px] text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                        />
                    </div>

                    {/* Active Flag Toggle */}
                    <div className="flex items-center justify-between bg-gray-50 dark:bg-[#31373f] rounded-xl p-4 border border-gray-100 dark:border-gray-700">
                        <div>
                            <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">Active flag</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Inactive flags are hidden everywhere</p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={flagForm.is_active}
                            onClick={() => setFlagForm(p => ({ ...p, is_active: !p.is_active }))}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${flagForm.is_active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}`}
                        >
                            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${flagForm.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>
                </div>
                )}
            </DetailsSidebar>

            {/* Add Appointment Modal */}
            <Modal
                isOpen={isAppointmentModalOpen}
                onClose={() => setIsAppointmentModalOpen(false)}
                title="Add Appointment"
                maxWidth="max-w-md"
                footer={(
                    <div className="flex gap-3 w-full">
                        <button
                            onClick={() => setIsAppointmentModalOpen(false)}
                            className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            form="appointment-form"
                            type="submit"
                            disabled={isSavingAppointment}
                            className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition text-sm shadow-md shadow-blue-200 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSavingAppointment ? 'Saving...' : 'Save Appointment'}
                        </button>
                    </div>
                )}
            >
                <form id="appointment-form" onSubmit={handleAddAppointment} className="space-y-4">
                    <div>
                        <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5" htmlFor="reason">
                            Reason
                        </label>
                        <input
                            type="text"
                            id="reason"
                            name="reason"
                            required
                            placeholder="e.g. Cardiology Appointment"
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#31373f] border border-gray-100 dark:border-gray-700 rounded-lg text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                            value={formData.reason}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5" htmlFor="date">
                                Date
                            </label>
                            <input
                                type="date"
                                id="date"
                                name="date"
                                required
                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#31373f] border border-gray-100 dark:border-gray-700 rounded-lg text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                value={formData.date}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5" htmlFor="time">
                                Time
                            </label>
                            <input
                                type="time"
                                id="time"
                                name="time"
                                required
                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#31373f] border border-gray-100 dark:border-gray-700 rounded-lg text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                value={formData.time}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[13px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5" htmlFor="description">
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            rows="4"
                            placeholder="Add details about the appointment..."
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#31373f] border border-gray-100 dark:border-gray-700 rounded-lg text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 resize-none"
                            value={formData.description}
                            onChange={handleInputChange}
                        />
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ResidenceDetails;